// src/screens/MentionsLegales.tsx
import React, { useEffect, useState } from "react";
import {
  Building2, UserCircle2, Server, Mail, Copyright as CopyrightIcon,
  ShieldCheck, Cookie, TriangleAlert, Link as LinkIcon, Scale, Sparkles, Flag,
} from "lucide-react";
import i18n from "../i18n";

// mini hook i18n safe
function useI18nSafe() {
  const [lng, setLng] = useState((i18n.resolvedLanguage || i18n.language || "fr").toLowerCase());
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

const LEGAL = {
  siteName: "SwishData",
  domain: "swishdata.app",
  publisherType: "Entreprise individuelle",
  publisherName: "SwishData",
  address: "312 chemin de la tuilière",
  siren: "990065401",
  intraTVA: "N° TVA intracommunautaire (si applicable)",
  directorName: "Cyprien Conseillon",
  contactEmail: "contact@swishdata.app",
  dpoEmail: "privacy@swishdata.app",
  hostName: "Netlify, Inc.",
  hostWebsite: "https://www.netlify.com",
  lastUpdate: "10/09/2026 "
};

const MentionsLegales: React.FC = () => {
  const { t } = useI18nSafe();

  return (
    <section className="relative min-h-screen w-full bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      {/* Fond déco */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute left-1/2 top-[-12rem] h-[28rem] w-[60rem] -translate-x-1/2 bg-gradient-to-r from-orange-500/20 via-fuchsia-500/20 to-cyan-500/20 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(255,255,255,0.16),_transparent_60%)] dark:bg-[radial-gradient(ellipse_at_top,_rgba(255,255,255,0.06),_transparent_60%)]" />
      </div>

      <div className="mx-auto max-w-4xl px-4 py-12">
        {/* En-tête */}
        <div className="mx-auto max-w-3xl rounded-2xl border border-white/40 bg-white/70 p-8 text-center shadow-lg backdrop-blur-md dark:border-white/5 dark:bg-white/5">
          <div className="mx-auto mb-2 inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <Sparkles className="h-4 w-4 text-orange-600 dark:text-orange-300" />
            <span>{t("legal:header.tag")}</span>
          </div>
          <h1 className="text-3xl font-bold italic text-orange-600 dark:text-orange-400">
            {LEGAL.siteName}
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            {t("legal:header.domain_label")}&nbsp;: {LEGAL.domain}
          </p>
        </div>

        {/* Contenu */}
        <div className="mt-8 space-y-6">
          {/* Éditeur */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("legal:sections.editor.title")}</h2>
            </header>
            <ul className="text-sm text-gray-700 dark:text-gray-300">
              <li><strong>{t("legal:labels.status")}&nbsp;:</strong> {LEGAL.publisherType}</li>
              <li><strong>{t("legal:labels.name")}&nbsp;:</strong> {LEGAL.publisherName}</li>
              <li><strong>{t("legal:labels.siren")}&nbsp;:</strong> {LEGAL.siren}</li>
            </ul>
          </div>

          {/* Directeur de la publication */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <UserCircle2 className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("legal:sections.director.title")}</h2>
            </header>
            <p className="text-sm text-gray-700 dark:text-gray-300">{LEGAL.directorName}</p>
          </div>

          {/* Hébergeur */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <Server className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("legal:sections.hosting.title")}</h2>
            </header>
            <ul className="text-sm text-gray-700 dark:text-gray-300">
              <li><strong>{t("legal:labels.host")}&nbsp;:</strong> {LEGAL.hostName}</li>
              <li>
                <strong>{t("legal:labels.website")}&nbsp;:</strong>{" "}
                <a className="underline hover:opacity-80" href={LEGAL.hostWebsite} target="_blank" rel="noreferrer">
                  {LEGAL.hostWebsite}
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <Mail className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("legal:sections.contact.title")}</h2>
            </header>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t("legal:sections.contact.body", { email: LEGAL.contactEmail })}
            </p>
          </div>

          {/* Propriété intellectuelle */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <CopyrightIcon className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("legal:sections.ip.title")}</h2>
            </header>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t("legal:sections.ip.body", { siteName: LEGAL.siteName })}
            </p>
          </div>

          {/* Données personnelles (RGPD) */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("legal:sections.gdpr.title")}</h2>
            </header>
            <div className="space-y-4 text-sm text-gray-700 dark:text-gray-300">
              <p>{t("legal:sections.gdpr.p1")}</p>

              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  {t("legal:sections.gdpr.categories_title")}
                </h3>
                <ul className="mt-1 list-disc space-y-1 pl-5">
                  <li>{t("legal:sections.gdpr.cat_account")}</li>
                  <li>{t("legal:sections.gdpr.cat_content")}</li>
                  <li>{t("legal:sections.gdpr.cat_technical")}</li>
                  <li>{t("legal:sections.gdpr.cat_purchase", { siteName: LEGAL.siteName })}</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  {t("legal:sections.gdpr.processors_title")}
                </h3>
                <ul className="mt-1 list-disc space-y-1 pl-5">
                  <li>{t("legal:sections.gdpr.proc_supabase")}</li>
                  <li>{t("legal:sections.gdpr.proc_revenuecat")}</li>
                  <li>{t("legal:sections.gdpr.proc_push")}</li>
                  <li>{t("legal:sections.gdpr.proc_emailjs")}</li>
                  <li>{t("legal:sections.gdpr.proc_stripe")}</li>
                  <li>{t("legal:sections.gdpr.proc_netlify")}</li>
                </ul>
                <p className="mt-2">{t("legal:sections.gdpr.transfer")}</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  {t("legal:sections.gdpr.retention_title")}
                </h3>
                <p className="mt-1">{t("legal:sections.gdpr.retention")}</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  {t("legal:sections.gdpr.rights_title")}
                </h3>
                <p className="mt-1">{t("legal:sections.gdpr.p2", { email: LEGAL.dpoEmail })}</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  {t("legal:sections.gdpr.minors_title")}
                </h3>
                <p className="mt-1">{t("legal:sections.gdpr.minors")}</p>
              </div>
            </div>
          </div>

          {/* Cookies */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <Cookie className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("legal:sections.cookies.title")}</h2>
            </header>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t("legal:sections.cookies.body")}
            </p>
          </div>

          {/* Responsabilité */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <TriangleAlert className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("legal:sections.liability.title")}</h2>
            </header>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t("legal:sections.liability.body")}
            </p>
          </div>

          {/* Liens externes */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <LinkIcon className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("legal:sections.links.title")}</h2>
            </header>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t("legal:sections.links.body")}
            </p>
          </div>

          {/* Droit applicable */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <Scale className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("legal:sections.law.title")}</h2>
            </header>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t("legal:sections.law.body")}
            </p>
          </div>

          {/* Signalement */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <Flag className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("legal:sections.report.title")}</h2>
            </header>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t("legal:sections.report.body", { email: LEGAL.contactEmail })}
            </p>
          </div>

          {/* Pied de page légal */}
          <div className="rounded-2xl border border-white/40 bg-white/70 p-6 text-center shadow-md backdrop-blur-md dark:border-white/5 dark:bg-white/5">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {t("legal:footer.last_update", { date: LEGAL.lastUpdate })}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MentionsLegales;
